import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { SEO_PAGES } from '../lib/site';

const page = SEO_PAGES.find((p) => p.path === '/pentagonos-mini-mental');

const ANSWER =
  'A tarefa dos pentágonos no Mini Mental explora habilidade visuoconstrutiva: a pessoa precisa reproduzir uma figura apresentada no material autorizado do instrumento. A pontuação deve seguir o critério da versão utilizada. Este site explica a finalidade e os cuidados de aplicação, mas não reproduz o estímulo gráfico nem substitui o material oficial.';

const RELATED = [
  { to: '/mini-exame-do-estado-mental', label: 'Mini Exame do Estado Mental', note: 'veja onde a habilidade visuoconstrutiva entra no conjunto do MEEM.' },
  { to: '/como-aplicar', label: 'Como aplicar o Mini Mental', note: 'preserve padronização e condições adequadas de execução.' },
  { to: '/erros-de-aplicacao', label: 'Erros de aplicação', note: 'evite pistas, reformulações e critérios improvisados.' },
  { to: '/adaptacoes-sensoriais', label: 'Adaptações sensoriais', note: 'entenda quando visão ou limitação motora comprometem a validade da tarefa.' },
  { to: '/interpretacao-dos-resultados', label: 'Pontuação e interpretação', note: 'integre o item ao escore total e ao contexto clínico.' }
];

export default function PentagonosMiniMental() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={[]}>
      <section aria-labelledby="o-que-avalia">
        <h2 id="o-que-avalia" className="text-2xl mb-5">O que o desenho dos pentágonos avalia?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          A tarefa é um componente visuoconstrutivo do MEEM. Em vez de testar apenas conhecimento verbal ou memória, ela exige perceber uma configuração visual e organizá-la graficamente.
        </p>
        <p className="text-[var(--text-muted)]">
          O desempenho pode ser influenciado por visão, coordenação motora, tremor, paresia, dor, dificuldade para segurar o lápis e compreensão da instrução. Por isso, um erro nessa tarefa não deve ser interpretado isoladamente como prova de déficit cognitivo específico.
        </p>
      </section>

      <section aria-labelledby="como-pontuar">
        <h2 id="como-pontuar" className="text-2xl mb-5">Como pontuar os pentágonos no Mini Mental?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          A regra prática é usar <strong>o critério da mesma versão do instrumento que foi aplicada</strong>. Não é adequado combinar um desenho de uma fonte com critérios de outra, nem criar uma regra visual própria porque o resultado “parece certo”.
        </p>
        <p className="text-[var(--text-muted)]">
          Registre o desempenho conforme o manual ou material autorizado e só depois incorpore o ponto ao total. A calculadora do MiniMental.com.br recebe o ponto já definido pelo aplicador; ela não decide se o desenho deve ou não receber pontuação.
        </p>
      </section>

      <section aria-labelledby="erros-comuns">
        <h2 id="erros-comuns" className="text-2xl mb-5">Erros comuns nessa tarefa</h2>
        <ul className="space-y-3 text-[var(--text-muted)] list-disc pl-5">
          <li>mostrar um estímulo redesenhado ou de procedência incerta;</li>
          <li>dar pistas sobre como organizar a figura;</li>
          <li>permitir tentativas extras sem que isso faça parte do protocolo usado;</li>
          <li>pontuar pela impressão subjetiva em vez do critério da versão;</li>
          <li>ignorar limitação visual ou motora relevante;</li>
          <li>converter o erro em diagnóstico isolado.</li>
        </ul>
      </section>

      <section aria-labelledby="figura">
        <h2 id="figura" className="text-2xl mb-5">Por que a figura não aparece nesta página?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          O estímulo faz parte do material do instrumento. Para evitar transformar uma página educacional em uma reprodução não autorizada do teste, o MiniMental.com.br não redesenha nem disponibiliza a figura como arquivo para impressão.
        </p>
        <p className="text-[var(--text-muted)]">
          Para obter o instrumento, consulte <Link to="/formulario-e-direitos-de-uso" className=" text-[var(--primary)] hover:underline">formulário e direitos de uso</Link>. Para a referência brasileira de Brucki et al. (2003), consulte também a página de <Link to="/mini-mental-pdf" className=" text-[var(--primary)] hover:underline">Mini Mental PDF</Link>.
        </p>
      </section>

      <section aria-labelledby="resultado">
        <h2 id="resultado" className="text-2xl mb-5">O que fazer com o resultado do item?</h2>
        <p className="text-[var(--text-muted)]">
          Some-o ao restante do MEEM conforme a versão aplicada e interprete o escore total dentro do contexto. Se visão, audição ou motricidade interferiram na execução, documente a limitação e evite comparar mecanicamente o total com uma referência como se as condições fossem equivalentes. Veja <Link to="/adaptacoes-sensoriais" className=" text-[var(--primary)] hover:underline">adaptações sensoriais</Link> e <Link to="/interpretacao-dos-resultados" className=" text-[var(--primary)] hover:underline">como interpretar a pontuação</Link>.
        </p>
      </section>
    </ArticleLayout>
  );
}
